import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useLocation } from "react-router-dom";
import { getMyCompany, updateMyCompany } from "api/Companies";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Tab, Tabs } from "components/ui/Tabs";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { CheckMarkIcon } from "components/svg";
import GeneralSettings from "./components/GeneralSettings";
import DocumentSettings from "./components/DocumentSettings";
import { Box, Spinner } from "theme-ui";
import InstantEstimateSettings from "./components/InstantEstimateSettings";
import Integrations from "./components/Integrations";
import Popups from "./components/Popups";
import ApiSettings from "./components/ApiSettings";
import LeadSettings from "./components/LeadSettings";
import FuelSurchargeSettings from "./components/FuelSurchargeSettings";
import ExpirationSettings from "containers/ExpirationSettings";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";

export default function Company() {
  const toast = useToast();
  const [company, setCompany] = useState<any>(null);
  const location = useLocation();
  const { user } = useContext<any>(UserContext);
  const lastTrackedTab = useRef<string | null>(null);
  // Tabs that read and write their own endpoint and have their own Save
  // button. On these the page-wide "Save Company" is hidden: it would not
  // save what is on screen (it only saves the company form), which is
  // exactly how enabling Leads looked saved and was not.
  const SELF_SAVING_TABS = ["leads", "expiration", "api"];
  const activeTab = new URLSearchParams(location.search).get("tab") || "general";
  const showCompanySave = !SELF_SAVING_TABS.includes(activeTab);

  const submitUpdate = async (values: any, actions: any) => {
    const previous = company;
    const changedFields: string[] = [];

    const isEqual = (a: any, b: any) => {
      if (a === b) return true;
      if (typeof a === "undefined" || a === null) {
        return typeof b === "undefined" || b === null;
      }
      if (typeof b === "undefined" || b === null) {
        return false;
      }
      try {
        return JSON.stringify(a) === JSON.stringify(b);
      } catch {
        return false;
      }
    };

    if (!isEqual(previous?.name, values?.name)) {
      changedFields.push("name");
    }

    const prevOwner = previous?.owner || {};
    const nextOwner = values?.owner || {};
    if (!isEqual(prevOwner?.firstName, nextOwner?.firstName)) {
      changedFields.push("owner.firstName");
    }
    if (!isEqual(prevOwner?.lastName, nextOwner?.lastName)) {
      changedFields.push("owner.lastName");
    }
    if (!isEqual(prevOwner?.email, nextOwner?.email)) {
      changedFields.push("owner.email");
    }

    const prevData = previous?.data || {};
    const nextData = values?.data || {};
    const dataKeys = new Set([
      ...Object.keys(prevData || {}),
      ...Object.keys(nextData || {}),
    ]);
    dataKeys.forEach((key) => {
      if (!isEqual(prevData?.[key], nextData?.[key])) {
        changedFields.push(`data.${key}`);
      }
    });

    const update = {
      name: values.name,
      owner: values.owner,
      data: {
        phone: values.data.phone,
        address: values.data.address,
        product_combinations: values.data.product_combinations,
        discount_popup: values.data.discount_popup,
        quoteTerms: values.data.quoteTerms,
        poTerms: values.data.poTerms,
        invoiceTerms: values.data.invoiceTerms,
        ...(company.data.logo && !values.data.logo ? { logo: "" } : {}),
      },
    };

    const res = await updateMyCompany({ update });
    if (res?.id) {
      toast.add("Company updated.", "var(--theme-ui-colors-success)");
      setCompany(res);

      if (changedFields.length > 0) {
        void trackActivity({
          module: "company_settings",
          action: "update_company_settings",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || res?.id || null,
          entityType: "company",
          entityId: res?.id ?? null,
          metadata: {
            changedFields,
          },
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    } else {
      toast.add(res?.message ? res.message : "Error updating company", "var(--theme-ui-colors-danger)");
    }
  };

  const UpdateSchema = Yup.object().shape({
    name: Yup.string().required("This field is required."),
  });

  const initCompany = async () => {
    const res = await getMyCompany();
    setCompany(res?.id ? res : null);
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Company";
    initCompany();
  }, []);

  useEffect(() => {
    if (!company?.id) return;
    const params = new URLSearchParams(location.search);
    const activeTab = params.get("tab") || "general";
    if (lastTrackedTab.current === activeTab) return;
    lastTrackedTab.current = activeTab;
    void trackActivity({
      module: "company_settings",
      action: "view_company_settings",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || company?.id || null,
      entityType: "company",
      entityId: company.id,
      metadata: { tab: activeTab },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [company?.id, location.search, user?.id, user?.company?.id]);

  return (
    <>
      <Header></Header>
      <Page title="Company Settings" subtitle="Edit Company wide settings.">
        {!company && (
          <Box sx={{ textAlign: "center" }}>
            <Spinner size={25} strokeWidth={3} title="loading" />
          </Box>
        )}
        {company && (
          <Formik initialValues={{ ...company }} onSubmit={submitUpdate} validationSchema={UpdateSchema} enableReinitialize>
            {({ isSubmitting, errors, values, submitCount, setFieldValue }) => (
              <Form noValidate autoComplete="off" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <Tabs
                  vertical
                  fillHeight
                  links={[
                    { key: "general", label: "General" },
                    { key: "document", label: "Document" },
                    { key: "payment", label: "Payment" },
                    { key: "instant_estimate", label: "Instant Estimate" },
                    { key: "popups", label: "Popups" },
                    { key: "expiration", label: "Expiration" },
                    { key: "fuel_surcharge", label: "Fuel Surcharge" },
                    // Leads module settings - only for roles that can edit
                    // leads. Not behind the module flag: this is where the
                    // module is switched on.
                    ...(((user as any)?.role?.permissions || []).includes("leadEdit") ? [{ key: "leads", label: "Leads" }] : []),
                    // Technical tabs stay last.
                    { key: "integrations", label: "Integrations" },
                    { key: "api", label: "API" },
                  ]}
                  stickyFooter
                  footer={
                    showCompanySave ? (
                      <BasicButton styles={{ margin: ".25rem 0 .25rem 0" }} type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save Company
                      </BasicButton>
                    ) : undefined
                  }
                >
                  <Tab itemKey="general">
                    <GeneralSettings {...{ company, errors, values, submitCount, setFieldValue, initCompany }} />
                  </Tab>
                  <Tab itemKey="document">
                    <DocumentSettings values={values} setFieldValue={setFieldValue} />
                  </Tab>
                  <Tab itemKey="payment">
                    <></>
                  </Tab>
                  <Tab itemKey="instant_estimate">
                    <InstantEstimateSettings {...{ company, errors, values, submitCount, setFieldValue, initCompany }} />
                  </Tab>
                  <Tab itemKey="integrations">
                    <Integrations {...{ company, errors, values, submitCount, setFieldValue, initCompany }} />
                  </Tab>
                  <Tab itemKey="popups">
                    <Popups {...{ company, errors, values, submitCount, setFieldValue, initCompany }} />
                  </Tab>
                  <Tab itemKey="api">
                    <ApiSettings />
                  </Tab>
                  <Tab itemKey="expiration">
                    <ExpirationSettings embedded />
                  </Tab>
                  <Tab itemKey="fuel_surcharge">
                    <FuelSurchargeSettings />
                  </Tab>
                  {((user as any)?.role?.permissions || []).includes("leadEdit") && (
                    <Tab itemKey="leads">
                      <LeadSettings />
                    </Tab>
                  )}
                </Tabs>
              </Form>
            )}
          </Formik>
        )}
      </Page>
    </>
  );
}
